/**
 * records.js — conversions for the Bet Tracker's stored record shape.
 *
 * STORED RECORD (single source of truth — what lives in Firestore at
 * users/{uid}/bets/{betId}, see betTrackerService.js):
 *
 *   {
 *     id,                       // Firestore doc id (attached on read)
 *     type: 'single' | 'parlay',
 *     legs: [{
 *       eventId?,               // Odds API event id (builder slips only)
 *       sport,                  // Odds API sport key when known
 *                               // ('americanfootball_nfl'), else a label
 *                               // ('NFL') from manual/paste/CSV entry
 *       market: 'h2h' | 'spreads' | 'totals',   // game legs
 *                               // prop legs (builder Phase 2) instead
 *                               // carry type: 'prop', an Odds API prop
 *                               // market key ('player_points'), plus
 *                               // player / side / line / statKeys /
 *                               // league / bookmaker for prop grading
 *       selection,              // display string ('Chiefs -3.5', 'Over 47.5')
 *       name,                   // outcome name ('Kansas City Chiefs', 'Over')
 *       point,                  // spread/total line from the backed
 *                               // side's perspective, or null (moneyline)
 *       american, decimal,      // leg odds (null on parsed parlay legs —
 *                               // share pages rarely price legs separately)
 *       homeTeam, awayTeam,
 *       commenceTime,           // ISO string
 *       result?,                // 'won' | 'lost' | 'push' once settled
 *       margin?                 // signed decision margin once settled
 *     }],
 *     stake,                    // dollars risked
 *     combinedDecimal, combinedAmerican,
 *     book?,                    // book display label ('BetRivers',
 *                               // 'DraftKings') when known
 *     source: 'parlay-builder' | 'manual' | 'paste' | 'csv' | 'screenshot',
 *     status: 'pending' | 'won' | 'lost' | 'push',
 *     payout?, profit?,         // written at settlement (derived values,
 *                               // stored once so list rendering is cheap;
 *                               // the stats engine re-derives the same
 *                               // numbers from odds + stake)
 *     margin?,                  // signed decision margin once settled
 *     settledBy?,               // 'auto' | 'manual' — how it was settled
 *     createdAt,                // ISO string
 *     settledAt?                // ISO string
 *   }
 *
 * The ported prototype engine (engine/model.js + engine/stats.js) works
 * on its own flatter bet shape and its math is NOT redesigned here:
 * toModelBet() maps a stored record into that shape, and
 * modelBetToRecord() maps prototype-shaped bets (parser drafts, CSV
 * rows) back. Grading semantics live in ingest/grading.js, untouched.
 */

import { americanToDecimal, round2, profitForBet, payoutForBet } from './engine/model.js';

export const SPORT_TITLES = Object.freeze({
  americanfootball_nfl: 'NFL',
  americanfootball_ncaaf: 'NCAAF',
  basketball_nba: 'NBA',
  basketball_ncaab: 'NCAAB',
  basketball_wnba: 'WNBA',
  baseball_mlb: 'MLB',
  icehockey_nhl: 'NHL',
  soccer_epl: 'Soccer',
  soccer_usa_mls: 'Soccer',
  mma_mixed_martial_arts: 'MMA',
  boxing_boxing: 'Boxing',
  tennis_atp: 'Tennis',
  golf_pga: 'Golf'
});

/** Best-effort label -> Odds API sport key, for grading manual bets. */
export const SPORT_KEY_BY_LABEL = Object.freeze({
  NFL: 'americanfootball_nfl',
  NCAAF: 'americanfootball_ncaaf',
  'COLLEGE FOOTBALL': 'americanfootball_ncaaf',
  NBA: 'basketball_nba',
  NCAAB: 'basketball_ncaab',
  'COLLEGE BASKETBALL': 'basketball_ncaab',
  WNBA: 'basketball_wnba',
  MLB: 'baseball_mlb',
  NHL: 'icehockey_nhl',
  MMA: 'mma_mixed_martial_arts',
  UFC: 'mma_mixed_martial_arts',
  SOCCER: 'soccer_epl',
  EPL: 'soccer_epl',
  MLS: 'soccer_usa_mls'
});

export const MARKET_TO_BET_TYPE = Object.freeze({
  h2h: 'moneyline',
  spreads: 'spread',
  totals: 'total'
});

export const BET_TYPE_TO_MARKET = Object.freeze({
  moneyline: 'h2h',
  spread: 'spreads',
  total: 'totals'
});

/** Odds API sport key for a stored sport value, or null if unknown. */
export function sportKeyForSport(sport) {
  if (!sport || typeof sport !== 'string') return null;
  if (sport.includes('_')) return sport;
  return SPORT_KEY_BY_LABEL[sport.trim().toUpperCase()] || null;
}

/** Display label for a stored sport value (key -> title, else as-is). */
export function sportLabelFor(sport) {
  if (!sport) return '';
  return SPORT_TITLES[sport] || sport;
}

export function decimalToAmerican(decimal) {
  if (typeof decimal !== 'number' || decimal <= 1) return null;
  if (decimal >= 2) return Math.round((decimal - 1) * 100);
  return Math.round(-100 / (decimal - 1));
}

function formatSignedPoint(point) {
  return point > 0 ? `+${point}` : `${point}`;
}

/** Display selection for a stored leg ('Chiefs -3.5', 'Over 47.5'). */
export function selectionLabel(leg) {
  if (leg.selection) return leg.selection;
  if (leg.market === 'totals') return `${leg.name} ${leg.point}`;
  if (leg.market === 'spreads' && typeof leg.point === 'number') {
    return `${leg.name} ${formatSignedPoint(leg.point)}`;
  }
  return leg.name || '';
}

export function legEventLabel(leg) {
  if (leg.awayTeam && leg.homeTeam) return `${leg.awayTeam} at ${leg.homeTeam}`;
  return leg.awayTeam || leg.homeTeam || '';
}

function slugify(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const round4 = (n) => Math.round((n + Number.EPSILON) * 10000) / 10000;

/**
 * Assemble a stored record from finished legs. Combined odds are the
 * product of leg decimals; callers parsing parlays whose legs carry no
 * individual prices pass combinedAmerican/combinedDecimal explicitly.
 */
export function buildRecord({
  legs,
  stake,
  book = null,
  source,
  status = 'pending',
  createdAt = new Date().toISOString(),
  combinedAmerican = null,
  combinedDecimal = null
}) {
  let decimal = combinedDecimal;
  if (decimal === null && combinedAmerican !== null) {
    decimal = americanToDecimal(combinedAmerican);
  }
  if (decimal === null) {
    decimal = 1;
    for (const leg of legs) {
      if (typeof leg.decimal !== 'number') { decimal = null; break; }
      decimal *= leg.decimal;
    }
  }
  const american = decimal !== null
    ? decimalToAmerican(decimal)
    : combinedAmerican;
  return {
    type: legs.length > 1 ? 'parlay' : 'single',
    legs,
    stake,
    combinedDecimal: decimal !== null ? round4(decimal) : null,
    combinedAmerican: american,
    ...(book ? { book } : {}),
    source,
    status,
    createdAt
  };
}

/**
 * Parlay Builder slip -> stored record. Builder legs (from the
 * parlay-builder function) already carry eventId / market / selection /
 * name / point / american / decimal / homeTeam / awayTeam /
 * commenceTime; `sport` is the builder's selected Odds API sport key
 * and `bookTitle` is the reference book's display title (payload
 * data.book.title).
 */
export function slipToBetRecord({ legs, sport, stake, bookTitle }) {
  const recordLegs = (legs || []).map((leg) => {
    const base = {
      eventId: leg.eventId,
      sport,
      market: leg.market,
      selection: leg.selection,
      name: leg.name,
      point: typeof leg.point === 'number' ? leg.point : null,
      american: leg.american,
      decimal: leg.decimal,
      homeTeam: leg.homeTeam,
      awayTeam: leg.awayTeam,
      commenceTime: leg.commenceTime
    };
    // Player-prop legs (Parlay Builder Phase 2) carry the fields
    // prop grading needs: who, which side, the stat mapping and
    // the book the price came from. Game legs are untouched.
    if (leg.type === 'prop') {
      return {
        ...base,
        type: 'prop',
        player: leg.player,
        side: leg.side,
        line: typeof leg.line === 'number' ? leg.line : null,
        statKeys: Array.isArray(leg.statKeys) ? leg.statKeys : [],
        league: leg.league || null,
        bookmaker: leg.bookmaker || null
      };
    }
    return base;
  });
  return buildRecord({
    legs: recordLegs,
    stake,
    book: bookTitle || null,
    source: 'parlay-builder'
  });
}

/** Split a prototype event string ('A at B', 'A vs. B') into teams. */
export function parseEventTeams(event) {
  const text = String(event || '').trim();
  const m = text.match(/^(.*?)\s+(?:at|vs\.?)\s+(.*)$/i);
  if (!m) return { awayTeam: text, homeTeam: '' };
  return { awayTeam: m[1].trim(), homeTeam: m[2].trim() };
}

/**
 * Outcome name from a prototype selection string: strip a trailing
 * spread/total line ('Kansas City Chiefs -3.5' -> 'Kansas City
 * Chiefs', 'Over 47.5' -> 'Over').
 */
export function nameFromSelection(selection, betType) {
  const text = String(selection || '').trim();
  if (betType === 'total') {
    const m = text.match(/^(over|under)\b/i);
    return m ? (m[1].toLowerCase() === 'over' ? 'Over' : 'Under') : text;
  }
  if (betType === 'spread') {
    return text.replace(/\s+[+-]\d+(?:\.\d+)?\s*$/, '').trim() || text;
  }
  return text;
}

function modelLegToRecordLeg(leg, parentBet) {
  const { awayTeam, homeTeam } = parseEventTeams(leg.event);
  const betType = leg.betType || 'moneyline';
  return {
    sport: parentBet.sport || '',
    market: BET_TYPE_TO_MARKET[betType] || 'h2h',
    selection: leg.selection || '',
    name: nameFromSelection(leg.selection, betType),
    point: typeof leg.line === 'number' ? leg.line : null,
    american: null,
    decimal: null,
    homeTeam,
    awayTeam,
    commenceTime: parentBet.commenceTime || parentBet.placedAt || null
  };
}

/**
 * Prototype-shaped bet (parser draft or CSV row) -> stored record.
 * Preserves settled status / settledAt / margin (CSV imports carry
 * history); cashed_out rows return null — the tracker has no cash-out
 * state, and the caller reports them as skipped rather than guessing
 * a won/lost mapping.
 */
export function modelBetToRecord(bet, source) {
  if (!bet || bet.status === 'cashed_out') return null;
  const isParlay = bet.betType === 'parlay' && Array.isArray(bet.legs) && bet.legs.length > 0;
  let legs;
  if (isParlay) {
    legs = bet.legs.map((leg) => modelLegToRecordLeg(leg, bet));
  } else {
    const { awayTeam, homeTeam } = parseEventTeams(bet.event);
    const betType = bet.betType || 'moneyline';
    legs = [{
      sport: bet.sport || '',
      market: BET_TYPE_TO_MARKET[betType] || 'h2h',
      selection: bet.selection || '',
      name: nameFromSelection(bet.selection, betType),
      point: typeof bet.line === 'number' ? bet.line : null,
      american: typeof bet.oddsAmerican === 'number' ? bet.oddsAmerican : null,
      decimal: typeof bet.oddsAmerican === 'number' ? americanToDecimal(bet.oddsAmerican) : null,
      homeTeam,
      awayTeam,
      commenceTime: bet.commenceTime || bet.placedAt || null
    }];
  }
  const record = buildRecord({
    legs,
    stake: bet.stake,
    book: bet.book && bet.book.name ? bet.book.name : null,
    source,
    status: ['won', 'lost', 'push'].includes(bet.status) ? bet.status : 'pending',
    createdAt: bet.placedAt || new Date().toISOString(),
    combinedAmerican: typeof bet.oddsAmerican === 'number' ? bet.oddsAmerican : null
  });
  if (record.status !== 'pending') {
    const profit = profitForBet(bet);
    const payout = payoutForBet(bet);
    if (profit !== null) record.profit = profit;
    if (payout !== null) record.payout = payout;
    if (typeof bet.margin === 'number') record.margin = bet.margin;
    record.settledAt = bet.settledAt || null;
    record.settledBy = 'manual';
  }
  return record;
}

/** Parser draft (prototype shape, status 'draft') -> stored record. */
export function draftToBetRecord(draft, source = 'paste') {
  if (!draft) return null;
  return modelBetToRecord({ ...draft, status: 'pending' }, source);
}

/** Stored record -> prototype engine bet (for stats.js / grading.js). */
export function toModelBet(record) {
  const legs = record.legs || [];
  const first = legs[0] || {};
  const isParlay = record.type === 'parlay';
  const modelLegs = legs.map((leg) => ({
    event: legEventLabel(leg),
    selection: selectionLabel(leg),
    // Prop legs keep their own betType: the score-based grader
    // must never mistake a player prop for a moneyline. autoGrade
    // settles prop legs from player stats before this shape is
    // consulted; anywhere else they land in needs_review, honestly.
    betType: leg.type === 'prop' ? 'prop' : (MARKET_TO_BET_TYPE[leg.market] || 'moneyline'),
    line: typeof leg.point === 'number' ? leg.point : null,
    ...(typeof leg.margin === 'number' ? { margin: leg.margin } : {})
  }));
  return {
    id: record.id,
    book: {
      key: record.book ? slugify(record.book) : 'unknown',
      name: record.book || 'Unknown book'
    },
    sport: sportLabelFor(first.sport),
    league: sportLabelFor(first.sport),
    event: isParlay ? `${legs.length}-leg parlay` : legEventLabel(first),
    selection: isParlay
      ? modelLegs.map((l) => l.selection).join(' + ')
      : (modelLegs[0] ? modelLegs[0].selection : ''),
    betType: isParlay ? 'parlay' : (MARKET_TO_BET_TYPE[first.market] || 'moneyline'),
    oddsAmerican: record.combinedAmerican,
    stake: record.stake,
    placedAt: record.createdAt,
    commenceTime: first.commenceTime || record.createdAt,
    settledAt: record.settledAt ?? null,
    status: record.status,
    line: isParlay ? null : (typeof first.point === 'number' ? first.point : null),
    margin: typeof record.margin === 'number' ? record.margin : null,
    ...(isParlay ? { legs: modelLegs } : {}),
    source: record.source
  };
}

function outcomeFromMargin(margin) {
  if (margin > 0) return 'won';
  if (margin < 0) return 'lost';
  return 'push';
}

/**
 * Firestore update fields for settling a record with a known outcome.
 * legMargins (optional, index-aligned with record.legs) fills each
 * leg's margin + result; payout/profit are derived from the combined
 * American odds and stake with the engine's rounding.
 */
export function settlementUpdates(record, outcome, {
  margin = null,
  legMargins = null,
  settledAt = new Date().toISOString(),
  settledBy = 'auto'
} = {}) {
  // Profit derives from combinedAmerican — the same input the ported
  // stats engine uses (profitForBet reads oddsAmerican) — so the
  // stored profit and the dashboard's derived profit can never drift
  // apart by rounding.
  let profit = null;
  if (outcome === 'won' && typeof record.combinedAmerican === 'number') {
    const decimal = americanToDecimal(record.combinedAmerican);
    if (decimal !== null) profit = round2(record.stake * (decimal - 1));
  } else if (outcome === 'lost') {
    profit = round2(-record.stake);
  } else if (outcome === 'push') {
    profit = 0;
  }
  const updates = {
    status: outcome,
    settledAt,
    settledBy,
    legs: (record.legs || []).map((leg, i) => {
      const legMargin = Array.isArray(legMargins) ? legMargins[i] : undefined;
      if (typeof legMargin !== 'number') return leg;
      return { ...leg, margin: legMargin, result: outcomeFromMargin(legMargin) };
    })
  };
  if (profit !== null) {
    updates.profit = profit;
    updates.payout = round2(record.stake + profit);
  }
  if (typeof margin === 'number') updates.margin = margin;
  return updates;
}

/**
 * Firestore update fields for a manual status override. For parlays a
 * won/lost override marks every leg with the bet outcome (that's what
 * the user is asserting); a push leaves leg results unset — which leg
 * pushed is unknowable from an override, and we don't guess.
 */
export function manualOverrideUpdates(record, outcome, settledAt = new Date().toISOString()) {
  const updates = settlementUpdates(record, outcome, { settledAt, settledBy: 'manual' });
  if (record.type === 'parlay' && (outcome === 'won' || outcome === 'lost')) {
    updates.legs = (record.legs || []).map((leg) => ({ ...leg, result: outcome }));
  } else if (record.type !== 'parlay') {
    updates.legs = (record.legs || []).map((leg) => ({ ...leg, result: outcome }));
  }
  return updates;
}
